var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { shallow } from 'zustand/shallow';
import { useLiFi, useWallet } from '../providers';
import { getUpdatedProcess, isRouteActive, isRouteDone, isRouteFailed, useRouteExecutionStore, useRouteExecutionStoreContext, } from '../stores';
import { WidgetEvent } from '../types/events';
import { deepClone } from '../utils';
import { useWidgetEvents } from './useWidgetEvents';
export const useRouteExecution = ({ routeId, executeInBackground, onAcceptExchangeRateUpdate, }) => {
    const lifi = useLiFi();
    const queryClient = useQueryClient();
    const { account, switchChain } = useWallet();
    const resumedAfterMount = useRef(false);
    const emitter = useWidgetEvents();
    const routeExecutionStoreContext = useRouteExecutionStoreContext();
    const routeExecution = useRouteExecutionStore((state) => state.routes[routeId]);
    const [updateRoute, restartRoute, deleteRoute] = useRouteExecutionStore((state) => [state.updateRoute, state.restartRoute, state.deleteRoute], shallow);
    const updateCallback = (updatedRoute) => {
        const routeExecution = routeExecutionStoreContext.getState().routes[updatedRoute.id];
        if (!routeExecution) {
            return;
        }
        const clonedUpdatedRoute = deepClone(updatedRoute);
        updateRoute(clonedUpdatedRoute);
        const process = getUpdatedProcess(routeExecution.route, clonedUpdatedRoute);
        if (process) {
            emitter.emit(WidgetEvent.RouteExecutionUpdated, {
                route: clonedUpdatedRoute,
                process,
            });
        }
        if (isRouteDone(clonedUpdatedRoute)) {
            emitter.emit(WidgetEvent.RouteExecutionCompleted, clonedUpdatedRoute);
        }
        if (isRouteFailed(clonedUpdatedRoute) && process) {
            emitter.emit(WidgetEvent.RouteExecutionFailed, {
                route: clonedUpdatedRoute,
                process,
            });
        }
        console.log('Route updated.', clonedUpdatedRoute);
    };
    const switchChainHook = (requiredChainId) => __awaiter(void 0, void 0, void 0, function* () {
        if (!account.isActive || !account.signer) {
            return account.signer;
        }
        const currentChainId = yield account.signer.getChainId();
        if (currentChainId !== requiredChainId) {
            const switched = yield switchChain(requiredChainId);
            if (!switched) {
                throw new Error('Chain was not switched.');
            }
        }
        return account.signer;
    });
    const acceptExchangeRateUpdateHook = (params) => __awaiter(void 0, void 0, void 0, function* () {
        if (!onAcceptExchangeRateUpdate) {
            return false;
        }
        const accepted = yield new Promise((resolve) => onAcceptExchangeRateUpdate(resolve, params));
        return accepted;
    });
    const executeRouteMutation = useMutation(() => {
        if (!account.signer) {
            throw Error('Account signer not found.');
        }
        if (!(routeExecution === null || routeExecution === void 0 ? void 0 : routeExecution.route)) {
            throw Error('Execution route not found.');
        }
        queryClient.removeQueries(['routes']);
        return lifi.executeRoute(account.signer, routeExecution.route, {
            updateCallback,
            switchChainHook,
            acceptExchangeRateUpdateHook,
            infiniteApproval: false,
            executeInBackground,
        });
    }, {
        onMutate: () => {
            console.log('Execution started.', routeId);
            if (routeExecution) {
                emitter.emit(WidgetEvent.RouteExecutionStarted, routeExecution.route);
            }
        },
    });
    const resumeRouteMutation = useMutation((resumedRoute) => {
        if (!account.signer) {
            throw Error('Account signer not found.');
        }
        if (!(routeExecution === null || routeExecution === void 0 ? void 0 : routeExecution.route)) {
            throw Error('Execution route not found.');
        }
        return lifi.resumeRoute(account.signer, resumedRoute !== null && resumedRoute !== void 0 ? resumedRoute : routeExecution.route, {
            updateCallback,
            switchChainHook,
            acceptExchangeRateUpdateHook,
            infiniteApproval: false,
            executeInBackground,
        });
    }, {
        onMutate: () => {
            console.log('Resumed to execution.', routeId);
        },
    });
    const executeRoute = useCallback(() => {
        executeRouteMutation.mutateAsync(undefined, {
            onError: (error) => {
                console.warn('Execution failed!', routeId, error);
                // Notification.showNotification(NotificationType.SwapExecution_ERROR);
            },
            onSuccess: (route) => {
                console.log('Executed successfully!', route);
                // Notification.showNotification(NotificationType.TRANSACTION_SUCCESSFULL);
            },
        });
    }, [executeRouteMutation, routeId]);
    const resumeRoute = useCallback((route) => {
        resumeRouteMutation.mutateAsync(route, {
            onError: (error) => {
                console.warn('Resumed execution failed.', routeId, error);
            },
            onSuccess: (route) => {
                console.log('Resumed execution successful.', route);
            },
        });
    }, [resumeRouteMutation, routeId]);
    const restartRouteMutation = useCallback(() => {
        restartRoute(routeId);
        resumeRoute(routeExecution === null || routeExecution === void 0 ? void 0 : routeExecution.route);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resumeRoute, routeExecution === null || routeExecution === void 0 ? void 0 : routeExecution.route, routeId]);
    const deleteRouteMutation = useCallback(() => {
        deleteRoute(routeId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeId]);
    // Resume route execution after page reload
    useEffect(() => {
        // Check if route is eligible for automatic resuming
        if (isRouteActive(routeExecution === null || routeExecution === void 0 ? void 0 : routeExecution.route) &&
            account.isActive &&
            !resumedAfterMount.current) {
            resumedAfterMount.current = true;
            resumeRoute();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [account.isActive]);
    useEffect(() => {
        return () => {
            var _a;
            const route = (_a = routeExecutionStoreContext.getState().routes[routeId]) === null || _a === void 0 ? void 0 : _a.route;
            if (!route || !isRouteActive(route)) {
                return;
            }
            lifi.updateRouteExecution(route, { executeInBackground: true });
            console.log('Move route execution to background.', routeId);
            resumedAfterMount.current = false;
        };
    }, [lifi, routeExecutionStoreContext, routeId]);
    return {
        executeRoute,
        restartRoute: restartRouteMutation,
        deleteRoute: deleteRouteMutation,
        route: routeExecution === null || routeExecution === void 0 ? void 0 : routeExecution.route,
        status: routeExecution === null || routeExecution === void 0 ? void 0 : routeExecution.status,
    };
};
